import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../api';
import { useAuth } from '../context/AuthContext';
import { isPrimarySpineSession, spineNodeForSession, stageFor } from '../components/projectSpineLifecycle';
import './SpecList.css';

function SpecList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;
  const repoId = searchParams.get('repo_id');

  const [repo, setRepo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [spineData, setSpineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [requirement, setRequirement] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  useEffect(() => {
    if (!userId || !repoId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [repoRes, sessionsRes, spineRes] = await Promise.all([
          fetch(`${API_URL}/api/repos/?user_id=${userId}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/specs/?user_id=${userId}&repository_id=${repoId}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/project-spine/${repoId}?user_id=${userId}`, { credentials: 'include' }),
        ]);
        const repos = await repoRes.json();
        const foundRepo = repos.find((r) => String(r.id) === String(repoId));
        setRepo(foundRepo || null);

        if (sessionsRes.ok) {
          setSessions(await sessionsRes.json());
        } else {
          const data = await sessionsRes.json();
          setError(data.detail || 'Failed to load spec sessions');
        }
        if (spineRes.ok) setSpineData(await spineRes.json());
      } catch (err) {
        setError('Network error — is the backend running?');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId, repoId]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!requirement.trim()) return;

    setStarting(true);
    setStartError(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/start?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repository_id: Number(repoId), requirement }),
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        navigate(`/specs/${data.id}`);
      } else {
        setStartError(data.detail || 'Failed to start spec session');
      }
    } catch (err) {
      setStartError('Network error — is the backend running?');
    } finally {
      setStarting(false);
    }
  };

  if (!repoId) {
    return (
      <div className="spec-list-page">
        <div className="empty-state">
          Missing repository context. <Link to="/dashboard">Go back to the dashboard</Link>.
        </div>
      </div>
    );
  }

  return (
    <div className="spec-list-page">
      <div className="spec-list-header">
        <div>
          <Link to="/dashboard" className="back-link">
            ← Dashboard
          </Link>
          <h1>Specs {repo ? `· ${repo.full_name}` : ''}</h1>
        </div>
        <div className="spec-list-header-actions">
          <Link to={`/board?repo_id=${repoId}`} className="btn-board-link">
            Board view
          </Link>
          <Link to={`/project-spine?repo_id=${repoId}`} className="btn-board-link">
            Business Intent
          </Link>
          <button type="button" className="btn-new-spec" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : '+ New Spec'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card new-spec-card">
          <h2>Describe what you need built</h2>
          <p>
            Write it informally — Forge's agent will ask clarifying questions before drafting a
            formal spec.
          </p>
          {startError && <div className="message error">{startError}</div>}
          <form onSubmit={handleStart}>
            <textarea
              className="requirement-input"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              placeholder="e.g. Add a /health endpoint that returns {status: ok}..."
              rows={5}
              autoFocus
            />
            <button type="submit" className="btn-create" disabled={starting || !requirement.trim()}>
              {starting ? 'Starting…' : 'Start Clarifying'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        {loading && <div className="empty-state">Loading…</div>}
        {error && <div className="message error">{error}</div>}

        {!loading && !error && sessions.length === 0 && (
          <div className="empty-state">
            No spec sessions yet for this repository. Start one above.
          </div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <ul className="session-list">
            {sessions.filter((s) => {
              const node = spineNodeForSession(spineData, s.id);
              return !node || isPrimarySpineSession(node, s.id);
            }).map((s) => {
              const node = spineNodeForSession(spineData, s.id);
              const meta = stageFor({ session: s, node });
              const context = new URLSearchParams(searchParams);
              context.set('repo_id', String(repoId));
              return (
                <li key={s.id}>
                  <Link to={`/specs/${s.id}?${context.toString()}`} className="session-card">
                    <div className="session-card-top">
                      <span className="session-title">
                        {s.title || `Session #${s.id}`}
                        {s.spec_number ? ` (${s.spec_number})` : ''}
                      </span>
                      <span className={`status-pill ${meta.color}`}>
                        <span className="status-dot" />
                        {meta.label}
                      </span>
                    </div>
                    {(s.spec_type || (s.spec_labels && s.spec_labels.length > 0)) && (
                      <div className="session-classification">
                        {s.spec_type && <span className="spec-type-badge">{s.spec_type}</span>}
                        {(s.spec_labels || []).map((label) => <span className="spec-label-badge" key={label}>{label}</span>)}
                      </div>
                    )}
                    {meta.spine && <p className="session-preview"><b>{meta.role}:</b> {meta.action}</p>}
                    {s.transcript && s.transcript.length > 0 && (
                      <p className="session-preview">
                        {s.transcript[0].content?.[0]?.text || ''}
                      </p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default SpecList;
